import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy, serverTimestamp, getDocs } from 'firebase/firestore';
import { detectAdminsByRole, notifyAllAdmins } from '../utils/adminNotificationSystem';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notificationListener, setNotificationListener] = useState(null);

  useEffect(() => {
    if (currentUser) {
      setupNotificationListener();
    } else {
      // Clean up when user logs out
      if (notificationListener) {
        notificationListener();
        setNotificationListener(null);
      }
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
    }

    return () => {
      if (notificationListener) {
        notificationListener();
      }
    };
  }, [currentUser]);

  const setupNotificationListener = () => {
    if (!currentUser) return;

    // Clean up existing listener
    if (notificationListener) {
      notificationListener();
    }

    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('Notifications snapshot received, size:', snapshot.size);
      const notificationsList = [];
      let unreadCounter = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Processing notification:', doc.id, data);
        const notification = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : new Date())
        };
        notificationsList.push(notification);
        
        if (!notification.read) {
          unreadCounter++;
        }
      });

      // Sort notifications by createdAt (newest first)
      notificationsList.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });

      console.log('Final notifications list:', notificationsList);
      console.log('Unread count:', unreadCounter);
      
      setNotifications(notificationsList);
      setUnreadCount(unreadCounter);
      setLoading(false);
    }, (error) => {
      console.error('Error listening to notifications:', error);
      setLoading(false);
    });

    setNotificationListener(() => unsubscribe);
  };

  // Create a new notification
  const createNotification = async (notificationData) => {
    try {
      console.log('Creating notification with data:', notificationData);
      const notification = {
        ...notificationData,
        createdAt: new Date(), // Use regular Date instead of serverTimestamp for better compatibility
        read: false,
        updatedAt: new Date()
      };

      console.log('Final notification object:', notification);
      const docRef = await addDoc(collection(db, 'notifications'), notification);
      console.log('Notification created successfully with ID:', docRef.id);
    } catch (error) {
      console.error('Error creating notification:', error);
      console.error('Error details:', error.message, error.code);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      const promises = unreadNotifications.map(notification =>
        updateDoc(doc(db, 'notifications', notification.id), {
          read: true,
          updatedAt: new Date()
        })
      );
      await Promise.all(promises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Helper function to create appointment notifications
  const createAppointmentNotification = async (appointmentData, type, recipientUserId) => {
    console.log('🔔 Creating appointment notification:', { appointmentData, type, recipientUserId });
    
    const notificationTypes = {
      'appointment_booked': {
        title: 'New Appointment Booked',
        message: `${appointmentData.userName} booked a ${appointmentData.service} appointment`,
        icon: '📅',
        category: 'appointment',
        priority: 'high'
      },
      'appointment_confirmed': {
        title: 'Appointment Confirmed',
        message: `Your ${appointmentData.service} appointment has been confirmed`,
        icon: '✅',
        category: 'appointment',
        priority: 'high'
      },
      'appointment_cancelled': {
        title: 'Appointment Cancelled',
        message: `Your ${appointmentData.service} appointment has been cancelled`,
        icon: '❌',
        category: 'appointment',
        priority: 'high'
      },
      'appointment_rescheduled': {
        title: 'Appointment Rescheduled',
        message: `Your ${appointmentData.service} appointment has been rescheduled`,
        icon: '🔄',
        category: 'appointment',
        priority: 'medium'
      }
    };

    const notificationTemplate = notificationTypes[type];
    if (!notificationTemplate) {
      console.error('❌ Invalid notification type:', type);
      return;
    }

    console.log('📝 Notification template found:', notificationTemplate);

    const notificationData = {
      userId: recipientUserId,
      title: notificationTemplate.title,
      message: notificationTemplate.message,
      icon: notificationTemplate.icon,
      category: notificationTemplate.category,
      priority: notificationTemplate.priority,
      relatedId: appointmentData.id,
      relatedType: 'appointment',
      actionUrl: recipientUserId === appointmentData.userId ? '/appointments' : '/admin/appointments'
    };

    console.log('📤 Sending notification with data:', notificationData);
    
    try {
      await createNotification(notificationData);
      console.log('✅ Appointment notification created successfully for user:', recipientUserId);
      
      // Si el destinatario no es un administrador, notificar a todos los administradores
      if (recipientUserId === appointmentData.userId && appointmentData.userId !== currentUser?.uid) {
        console.log('📢 Notificando a todos los administradores sobre esta cita...');
        
        // Crear notificación para administradores (sin userId)
        const adminNotificationData = {
          title: `${notificationTemplate.title} - ${appointmentData.userName}`,
          message: notificationTemplate.message,
          icon: notificationTemplate.icon,
          category: notificationTemplate.category,
          priority: notificationTemplate.priority,
          relatedId: appointmentData.id,
          relatedType: 'appointment',
          actionUrl: '/admin/appointments'
        };
        
        // Notificar a todos los administradores
        await notifyAllAdmins(adminNotificationData, createNotification);
      }
    } catch (error) {
      console.error('❌ Error creating appointment notification:', error);
      throw error;
    }
  };

  // Helper function to create estimate notifications
  const createEstimateNotification = async (estimateData, type, recipientUserId) => {
    try {
      console.log('🔔 Creating estimate notification:', { estimateData, type, recipientUserId });
      
      // Ensure we have a valid recipient ID
      if (!recipientUserId) {
        console.error('❌ Recipient user ID is null or undefined');
        return false;
      }
      
      // Define notification types
      const notificationTypes = {
        'estimate_created': {
          title: 'New Estimate Request',
          message: `A new estimate request has been submitted`,
          icon: '📋',
          category: 'estimate',
          priority: 'high'
        },
        'estimate_updated': {
          title: 'Estimate Updated',
          message: `Your estimate request has been updated`,
          icon: '📝',
          category: 'estimate',
          priority: 'medium'
        },
        'estimate_quoted': {
          title: 'Estimate Quote Ready',
          message: `Your estimate quote is ready to review`,
          icon: '💵',
          category: 'estimate',
          priority: 'high'
        },
        'signature_requested': {
          title: 'Signature Requested',
          message: `Please sign your estimate to proceed`,
          icon: '✍️',
          category: 'signature',
          priority: 'high'
        },
        'signature_completed': {
          title: 'Signature Completed',
          message: `Estimate has been signed successfully`,
          icon: '✅',
          category: 'signature',
          priority: 'high'
        },
        'message_received': {
          title: 'New Message',
          message: `You have a new message about your estimate`,
          icon: '💬',
          category: 'message',
          priority: 'medium'
        },
        'estimate_submitted': {
          title: 'New Estimate Submitted',
          message: `A new estimate has been submitted`,
          icon: '📋',
          category: 'estimate',
          priority: 'high'
        },
        'estimate_message': {
          title: 'New Estimate Message',
          message: `New message about an estimate`,
          icon: '💬',
          category: 'message',
          priority: 'medium'
        }
      };
      
      // Get notification template
      const template = notificationTypes[type] || {
        title: type.replace('_', ' ').charAt(0).toUpperCase() + type.replace('_', ' ').slice(1),
        message: `Notification about estimate ${estimateData.id}`,
        icon: '🔔',
        category: 'estimate',
        priority: 'medium'
      };
      
      console.log('📝 Using notification template:', template);
      
      // Create notification data
      const notificationData = {
        userId: recipientUserId,
        title: template.title,
        message: template.message,
        icon: template.icon,
        category: template.category,
        priority: template.priority,
        relatedId: estimateData.id,
        relatedType: 'estimate',
        actionUrl: recipientUserId === estimateData.userId ? '/estimates' : '/admin/estimates'
      };
      
      console.log('📤 Sending notification with data:', notificationData);
      
      await createNotification(notificationData);
      console.log('✅ Estimate notification created successfully for user:', recipientUserId);
      
      // Si el destinatario es un cliente (no admin), notificar a todos los administradores
      if (recipientUserId === estimateData.userId) {
        console.log('📢 Notificando a todos los administradores sobre este presupuesto...');
        
        // Crear notificación para administradores (sin userId)
        const adminNotificationData = {
          title: `${template.title} - ${estimateData.userName || 'Cliente'}`,
          message: `${template.message} por ${estimateData.userName || estimateData.userEmail || 'un cliente'}`,
          icon: template.icon,
          category: template.category,
          priority: template.priority,
          relatedId: estimateData.id,
          relatedType: 'estimate',
          actionUrl: '/admin/estimates'
        };
        
        // Notificar a todos los administradores
        await notifyAllAdmins(adminNotificationData, createNotification);
      }
      
      return true;
    } catch (error) {
      console.error('❌ Error creating estimate notification:', error);
      console.error('Error details:', error.message, error.code);
      return false;
    }
  };

  // Helper function to get admin user IDs - Ahora usa el nuevo sistema basado en roles
  const getAdminUserIds = async () => {
    try {
      console.log('🔍 Buscando usuarios administradores por rol...');
      
      // Usar el nuevo sistema de detección de administradores por rol
      const adminIds = await detectAdminsByRole();
      
      if (adminIds && adminIds.length > 0) {
        console.log('✅ Administradores detectados por rol:', adminIds);
        return adminIds;
      }
      
      // Si no se encontraron administradores, usar el fallback del archivo .env
      console.warn('⚠️ No se encontraron administradores por rol');
      console.log('🔧 Intentando usar REACT_APP_FALLBACK_ADMIN_UID del archivo .env...');
      
      const fallbackAdminId = process.env.REACT_APP_FALLBACK_ADMIN_UID;
      
      if (!fallbackAdminId) {
        console.error('❌ REACT_APP_FALLBACK_ADMIN_UID no está configurado en .env');
        
        // Último recurso: utilizar el usuario actual como administrador
        if (currentUser?.uid) {
          console.log('✅ Usando usuario actual como administrador de emergencia:', currentUser.uid);
          return [currentUser.uid];
        }
        
        console.error('❌ No se pudo encontrar ningún administrador');
        return []; // Devolver array vacío si no hay administradores disponibles
      }
      
      console.log('✅ Usando administrador del archivo .env:', fallbackAdminId);
      return [fallbackAdminId];
    } catch (error) {
      console.error('❌ Error buscando administradores:', error);
      
      // Intentar usar el ID de administrador del archivo .env en caso de error
      const fallbackAdminId = process.env.REACT_APP_FALLBACK_ADMIN_UID;
      if (fallbackAdminId) {
        console.warn('🔄 Usando administrador de fallback debido a error:', fallbackAdminId);
        return [fallbackAdminId];
      }
      
      // Usar el usuario actual como fallback si no hay otras opciones
      if (currentUser?.uid) {
        console.warn('🔄 Usando usuario actual como administrador de emergencia:', currentUser.uid);
        return [currentUser.uid];
      }
      
      console.error('❌ No hay administradores disponibles');
      return []; // Devolver array vacío si no hay fallbacks
    }
  };

  // Emergency function to get all possible admin users - GUARANTEED TO FIND AT LEAST ONE
  const getAllPossibleAdminIds = async () => {
    try {
      console.log('🚨 Emergency: Searching for all possible admin users...');
      const allAdminIds = new Set();
      
      // Try role = 'admin'
      try {
        const usersRef = collection(db, 'users');
        const roleQuery = query(usersRef, where('role', '==', 'admin'));
        const roleSnapshot = await getDocs(roleQuery);
        roleSnapshot.docs.forEach(doc => allAdminIds.add(doc.id));
        console.log('👥 Found admins via role:', Array.from(allAdminIds));
      } catch (e) {
        console.warn('⚠️ Error searching by role:', e);
      }
      
      // Try isAdmin = true
      try {
        const usersRef = collection(db, 'users');
        const isAdminQuery = query(usersRef, where('isAdmin', '==', true));
        const isAdminSnapshot = await getDocs(isAdminQuery);
        isAdminSnapshot.docs.forEach(doc => allAdminIds.add(doc.id));
        console.log('👥 Found admins via isAdmin:', Array.from(allAdminIds));
      } catch (e) {
        console.warn('⚠️ Error searching by isAdmin:', e);
      }
      
      // Try to get all users and filter for potential admins
      try {
        const usersRef = collection(db, 'users');
        const allUsersSnapshot = await getDocs(usersRef);
        allUsersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          // Check for any admin-like properties
          if (userData.role === 'admin' || 
              userData.isAdmin === true || 
              userData.admin === true ||
              userData.userType === 'admin' ||
              userData.type === 'admin') {
            allAdminIds.add(doc.id);
          }
        });
        console.log('👥 Found admins via comprehensive search:', Array.from(allAdminIds));
      } catch (e) {
        console.warn('⚠️ Error in comprehensive search:', e);
      }
      
      // ALWAYS add current user as fallback
      if (currentUser?.uid) {
        allAdminIds.add(currentUser.uid);
        console.log('👤 Added current user to admin list (GUARANTEED)');
      }
      
      const finalAdminIds = Array.from(allAdminIds);
      console.log('🎯 Final admin IDs found:', finalAdminIds);
      
      // GUARANTEE: If we still have no admins, create a fallback
      if (finalAdminIds.length === 0) {
        console.warn('🚨 No admins found - creating emergency fallback');
        const emergencyAdminId = 'emergency-admin-' + Date.now();
        console.log('🚨 Created emergency admin ID:', emergencyAdminId);
        return [emergencyAdminId];
      }
      
      return finalAdminIds;
    } catch (error) {
      console.error('❌ Critical error in emergency admin search:', error);
      // GUARANTEED FALLBACK: Always return at least the current user
      if (currentUser?.uid) {
        console.log('🚨 Using current user as emergency fallback:', currentUser.uid);
        return [currentUser.uid];
      }
      
      // Last resort: create a temporary admin ID
      const emergencyAdminId = 'emergency-admin-' + Date.now();
      console.log('🚨 Created emergency admin ID as last resort:', emergencyAdminId);
      return [emergencyAdminId];
    }
  };

  // Force refresh notifications
  const refreshNotifications = async () => {
    try {
      console.log('🔄 Force refreshing notifications...');
      if (currentUser) {
        setupNotificationListener();
      }
    } catch (error) {
      console.error('❌ Error refreshing notifications:', error);
    }
  };

  // GUARANTEED notification delivery - sends to ALL users who might be admins
  const sendGuaranteedAdminNotification = async (notificationData) => {
    try {
      console.log('🚨 GUARANTEED: Sending notification to all possible admins...');
      
      // Get all possible admin IDs
      const allAdminIds = await getAllPossibleAdminIds();
      console.log('🎯 Admin IDs for guaranteed delivery:', allAdminIds);
      
      let successCount = 0;
      let errorCount = 0;
      
      // Send to each admin
      for (const adminId of allAdminIds) {
        try {
          console.log(`📤 GUARANTEED: Sending to admin ${adminId}`);
          await createNotification({
            userId: adminId,
            ...notificationData
          });
          console.log(`✅ GUARANTEED: Sent to admin ${adminId}`);
          successCount++;
        } catch (error) {
          console.error(`❌ GUARANTEED: Failed to send to admin ${adminId}:`, error);
          errorCount++;
        }
      }
      
      console.log(`🎉 GUARANTEED delivery summary: ${successCount} successful, ${errorCount} failed`);
      return { successCount, errorCount };
    } catch (error) {
      console.error('❌ GUARANTEED delivery failed:', error);
      return { successCount: 0, errorCount: 1 };
    }
  };

  // Función para notificar a todos los administradores
  const notifyAdmins = async (notificationData) => {
    try {
      console.log('📢 Notificando a todos los administradores...');
      return await notifyAllAdmins(notificationData, createNotification);
    } catch (error) {
      console.error('❌ Error notificando a administradores:', error);
      return { success: 0, failed: 1 };
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    createNotification,
    markAsRead,
    markAllAsRead,
    createAppointmentNotification,
    createEstimateNotification,
    getAdminUserIds,
    getAllPossibleAdminIds,
    refreshNotifications,
    sendGuaranteedAdminNotification,
    notifyAdmins
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
